export const privacity: Record<
  'en' | 'es' | 'fr' | 'pt',
  {
    language: string
    privacy_policy: {
      title: string
      effective_date: string
      intro: {
        company: string
        explanation: string
        agreement: string
      }
      information_collection: {
        title: string
        description: string
      }
      types_of_data: {
        title: string
        personal_data: {
          title: string
          description: string
          items: string[]
        }
        usage_data: {
          title: string
          description_browser: string
          description_mobile: string
        }
        tracking_cookies: {
          title: string
          description: string
          explanation: string
          browser_instructions: string
          cookie_types: {
            session: {title: string; description: string}
            preference: {title: string; description: string}
            security: {title: string; description: string}
          }
        }
      }
      use_of_data: {
        title: string
        purposes: string[]
      }
      transfer_of_data: {
        title: string
        description: string
        international: string
        consent: string
        security: string
      }
      disclosure_of_data: {
        title: string
        legal_requirements: {
          title: string
          description: string
          reasons: string[]
        }
      }
      security_of_data: {
        title: string
        description: string
      }
      service_providers: {
        title: string
        description: string
        obligations: string
      }
      links_to_other_sites: {
        title: string
        description: string
        disclaimer: string
      }
      childrens_privacy: {
        title: string
        description: string
        parental_notice: string
      }
      changes_to_policy: {
        title: string
        notification: string
        effective_date: string
        review_advice: string
      }
      contact_us: {
        title: string
        description: string
        email: string
        website: string
      }
    }
  }
> = {
  en: {
    language: 'Select Language',
    privacy_policy: {
      title: 'Privacy Policy',
      effective_date: 'Effective date: April 03, 2025',
      intro: {
        company:
          'Panel Track LLC ("us", "we", or "our") operates the website panel-track.com and the PanelTrack mobile application (the "Service").',
        explanation:
          'This page informs you of our policies regarding the collection, use, and disclosure of personal data when you use our Service and the choices you have associated with that data.',
        agreement:
          'We use your data to provide and improve the Service. By using the Service, you agree to the collection and use of information in accordance with this policy. Unless otherwise defined in this Privacy Policy, terms used in this Privacy Policy have the same meanings as in our Terms and Conditions.',
      },
      information_collection: {
        title: 'Information Collection And Use',
        description:
          'We collect several different types of information for various purposes to provide and improve our Service to you.',
      },
      types_of_data: {
        title: 'Types of Data Collected',
        personal_data: {
          title: 'Personal Data',
          description:
            'While using our Service, we may ask you to provide us with certain personally identifiable information that can be used to contact or identify you ("Personal Data"). Personally identifiable information may include, but is not limited to:',
          items: ['Email address', 'Cookies and Usage Data'],
        },
        usage_data: {
          title: 'Usage Data',
          description_browser:
            'We may also collect information that your browser sends whenever you visit our Service or when you access the Service by or through a mobile device ("Usage Data"). This Usage Data may include information such as your computer\'s Internet Protocol address (e.g. IP address), browser type, browser version, the pages of our Service that you visit, the time and date of your visit, the time spent on those pages, unique device identifiers and other diagnostic data.',
          description_mobile:
            'When you access the Service by or through a mobile device, this Usage Data may include information such as the type of mobile device you use, your mobile device unique ID, the IP address of your mobile device, your mobile operating system, the type of mobile Internet browser you use, unique device identifiers and other diagnostic data.',
        },
        tracking_cookies: {
          title: 'Tracking & Cookies Data',
          description:
            'We use cookies and similar tracking technologies to track the activity on our Service and hold certain information.',
          explanation:
            'Cookies are files with small amount of data which may include an anonymous unique identifier. Cookies are sent to your browser from a website and stored on your device. Tracking technologies also used are beacons, tags, and scripts to collect and track information and to improve and analyze our Service.',
          browser_instructions:
            'You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our Service.',
          cookie_types: {
            session: {
              title: 'Session Cookies',
              description: 'We use Session Cookies to operate our Service.',
            },
            preference: {
              title: 'Preference Cookies',
              description:
                'We use Preference Cookies to remember your preferences and various settings.',
            },
            security: {
              title: 'Security Cookies',
              description: 'We use Security Cookies for security purposes.',
            },
          },
        },
      },
      use_of_data: {
        title: 'Use of Data',
        purposes: [
          'To provide and maintain the Service',
          'To notify you about changes to our Service',
          'To allow you to participate in interactive features of our Service when you choose to do so',
          'To provide customer care and support',
          'To provide analysis or valuable information so that we can improve the Service',
          'To monitor the usage of the Service',
          'To detect, prevent and address technical issues',
        ],
      },
      transfer_of_data: {
        title: 'Transfer Of Data',
        description:
          'Your information, including Personal Data, may be transferred to — and maintained on — computers located outside of your state, province, country or other governmental jurisdiction where the data protection laws may differ than those from your jurisdiction.',
        international:
          'If you are located outside United States of America and choose to provide information to us, please note that we transfer the data, including Personal Data, to United States of America and process it there.',
        consent:
          'Your consent to this Privacy Policy followed by your submission of such information represents your agreement to that transfer.',
        security:
          'Panel Track LLC will take all steps reasonably necessary to ensure that your data is treated securely and in accordance with this Privacy Policy and no transfer of your Personal Data will take place to an organization or a country unless there are adequate controls in place including the security of your data and other personal information.',
      },
      disclosure_of_data: {
        title: 'Disclosure Of Data',
        legal_requirements: {
          title: 'Legal Requirements',
          description:
            'Panel Track LLC may disclose your Personal Data in the good faith belief that such action is necessary to:',
          reasons: [
            'To comply with a legal obligation',
            'To protect and defend the rights or property of Panel Track LLC',
            'To prevent or investigate possible wrongdoing in connection with the Service',
            'To protect the personal safety of users of the Service or the public',
            'To protect against legal liability',
          ],
        },
      },
      security_of_data: {
        title: 'Security Of Data',
        description:
          'The security of your data is important to us, but remember that no method of transmission over the Internet, or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your Personal Data, we cannot guarantee its absolute security.',
      },
      service_providers: {
        title: 'Service Providers',
        description:
          'We may employ third party companies and individuals to facilitate our Service ("Service Providers"), to provide the Service on our behalf, to perform Service-related services or to assist us in analyzing how our Service is used.',
        obligations:
          'These third parties have access to your Personal Data only to perform these tasks on our behalf and are obligated not to disclose or use it for any other purpose.',
      },
      links_to_other_sites: {
        title: 'Links To Other Sites',
        description:
          "Our Service may contain links to other sites that are not operated by us. If you click on a third party link, you will be directed to that third party's site. We strongly advise you to review the Privacy Policy of every site you visit.",
        disclaimer:
          'We have no control over and assume no responsibility for the content, privacy policies or practices of any third party sites or services.',
      },
      childrens_privacy: {
        title: "Children's Privacy",
        description: 'Our Service does not address anyone under the age of 18 ("Children").',
        parental_notice:
          'We do not knowingly collect personally identifiable information from anyone under the age of 18. If you are a parent or guardian and you are aware that your Children has provided us with Personal Data, please contact us. If we become aware that we have collected Personal Data from children without verification of parental consent, we take steps to remove that information from our servers.',
      },
      changes_to_policy: {
        title: 'Changes To This Privacy Policy',
        notification:
          'We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page.',
        effective_date:
          'We will let you know via email and/or a prominent notice on our Service, prior to the change becoming effective and update the "effective date" at the top of this Privacy Policy.',
        review_advice:
          'You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.',
      },
      contact_us: {
        title: 'Contact Us',
        description: 'If you have any questions about this Privacy Policy, please contact us:',
        email: 'By email: pako@total-factory.com',
        website: 'By visiting this page on our website: panel-track.com',
      },
    },
  },
  es: {
    language: 'Seleccionar idioma',
    privacy_policy: {
      title: 'Política de Privacidad',
      effective_date: 'Fecha de entrada en vigor: 3 de abril de 2025',
      intro: {
        company:
          'Panel Track LLC ("nosotros", "nos" o "nuestro") opera el sitio web panel-track.com y la aplicación móvil PanelTrack (el "Servicio").',
        explanation:
          'Esta página le informa sobre nuestras políticas con respecto a la recopilación, uso y divulgación de datos personales cuando utiliza nuestro Servicio y las opciones que tiene asociadas con esos datos.',
        agreement:
          'Utilizamos sus datos para proporcionar y mejorar el Servicio. Al utilizar el Servicio, acepta la recopilación y el uso de información de acuerdo con esta política. A menos que se defina de otra manera en esta Política de Privacidad, los términos utilizados en esta Política de Privacidad tienen los mismos significados que en nuestros Términos y Condiciones.',
      },
      information_collection: {
        title: 'Recopilación y Uso de la Información',
        description:
          'Recopilamos varios tipos diferentes de información con diversos propósitos para proporcionar y mejorar nuestro Servicio.',
      },
      types_of_data: {
        title: 'Tipos de Datos Recopilados',
        personal_data: {
          title: 'Datos Personales',
          description:
            'Mientras utiliza nuestro Servicio, podemos pedirle que nos proporcione cierta información de identificación personal que puede usarse para contactarlo o identificarlo ("Datos Personales"). La información de identificación personal puede incluir, pero no se limita a:',
          items: ['Dirección de correo electrónico', 'Cookies y Datos de Uso'],
        },
        usage_data: {
          title: 'Datos de Uso',
          description_browser:
            'También podemos recopilar información que su navegador envía cada vez que visita nuestro Servicio o cuando accede al Servicio a través de un dispositivo móvil ("Datos de Uso"). Estos Datos de Uso pueden incluir información como la dirección del Protocolo de Internet de su computadora (por ejemplo, dirección IP), tipo de navegador, versión del navegador, las páginas de nuestro Servicio que visita, la hora y fecha de su visita, el tiempo dedicado a esas páginas, identificadores únicos de dispositivos y otros datos de diagnóstico.',
          description_mobile:
            'Cuando accede al Servicio a través de un dispositivo móvil, estos Datos de Uso pueden incluir información como el tipo de dispositivo móvil que utiliza, el ID único de su dispositivo móvil, la dirección IP de su dispositivo móvil, su sistema operativo móvil, el tipo de navegador de Internet móvil que utiliza, identificadores únicos de dispositivos y otros datos de diagnóstico.',
        },
        tracking_cookies: {
          title: 'Datos de Seguimiento y Cookies',
          description:
            'Utilizamos cookies y tecnologías de seguimiento similares para rastrear la actividad en nuestro Servicio y mantener cierta información.',
          explanation:
            'Las cookies son archivos con una pequeña cantidad de datos que pueden incluir un identificador único anónimo. Las cookies se envían a su navegador desde un sitio web y se almacenan en su dispositivo. Las tecnologías de seguimiento también utilizadas son balizas, etiquetas y scripts para recopilar y rastrear información y para mejorar y analizar nuestro Servicio.',
          browser_instructions:
            'Puede indicar a su navegador que rechace todas las cookies o que indique cuándo se envía una cookie. Sin embargo, si no acepta cookies, es posible que no pueda utilizar algunas partes de nuestro Servicio.',
          cookie_types: {
            session: {
              title: 'Cookies de Sesión',
              description: 'Utilizamos Cookies de Sesión para operar nuestro Servicio.',
            },
            preference: {
              title: 'Cookies de Preferencias',
              description:
                'Utilizamos Cookies de Preferencias para recordar sus preferencias y varios ajustes.',
            },
            security: {
              title: 'Cookies de Seguridad',
              description: 'Utilizamos Cookies de Seguridad por motivos de seguridad.',
            },
          },
        },
      },
      use_of_data: {
        title: 'Uso de Datos',
        purposes: [
          'Para proporcionar y mantener el Servicio',
          'Para notificarle sobre cambios en nuestro Servicio',
          'Para permitirle participar en funciones interactivas de nuestro Servicio cuando elija hacerlo',
          'Para brindar atención y soporte al cliente',
          'Para proporcionar análisis o información valiosa para que podamos mejorar el Servicio',
          'Para monitorear el uso del Servicio',
          'Para detectar, prevenir y abordar problemas técnicos',
        ],
      },
      transfer_of_data: {
        title: 'Transferencia de Datos',
        description:
          'Su información, incluidos los Datos Personales, puede transferirse y mantenerse en computadoras ubicadas fuera de su estado, provincia, país u otra jurisdicción gubernamental donde las leyes de protección de datos pueden diferir de las de su jurisdicción.',
        international:
          'Si se encuentra fuera de los Estados Unidos de América y decide proporcionarnos información, tenga en cuenta que transferimos los datos, incluidos los Datos Personales, a los Estados Unidos de América y los procesamos allí.',
        consent:
          'Su consentimiento a esta Política de Privacidad seguido de su envío de dicha información representa su acuerdo con esa transferencia.',
        security:
          'Panel Track LLC tomará todas las medidas razonablemente necesarias para garantizar que sus datos sean tratados de manera segura y de acuerdo con esta Política de Privacidad, y no se realizará ninguna transferencia de sus Datos Personales a una organización o país a menos que existan controles adecuados, incluida la seguridad de sus datos y otra información personal.',
      },
      disclosure_of_data: {
        title: 'Divulgación de Datos',
        legal_requirements: {
          title: 'Requisitos Legales',
          description:
            'Panel Track LLC puede divulgar sus Datos Personales de buena fe creyendo que dicha acción es necesaria para:',
          reasons: [
            'Cumplir con una obligación legal',
            'Proteger y defender los derechos o la propiedad de Panel Track LLC',
            'Prevenir o investigar posibles irregularidades en relación con el Servicio',
            'Proteger la seguridad personal de los usuarios del Servicio o del público',
            'Protegerse contra la responsabilidad legal',
          ],
        },
      },
      security_of_data: {
        title: 'Seguridad de los Datos',
        description:
          'La seguridad de sus datos es importante para nosotros, pero recuerde que ningún método de transmisión por Internet o método de almacenamiento electrónico es 100% seguro. Si bien nos esforzamos por utilizar medios comercialmente aceptables para proteger sus Datos Personales, no podemos garantizar su seguridad absoluta.',
      },
      service_providers: {
        title: 'Proveedores de Servicios',
        description:
          'Podemos emplear empresas e individuos de terceros para facilitar nuestro Servicio ("Proveedores de Servicios"), para proporcionar el Servicio en nuestro nombre, para realizar servicios relacionados con el Servicio o para ayudarnos a analizar cómo se utiliza nuestro Servicio.',
        obligations:
          'Estos terceros tienen acceso a sus Datos Personales solo para realizar estas tareas en nuestro nombre y están obligados a no divulgarlos ni utilizarlos para ningún otro fin.',
      },
      links_to_other_sites: {
        title: 'Enlaces a Otros Sitios',
        description:
          'Nuestro Servicio puede contener enlaces a otros sitios que no son operados por nosotros. Si hace clic en un enlace de terceros, será dirigido al sitio de ese tercero. Le recomendamos encarecidamente que revise la Política de Privacidad de cada sitio que visite.',
        disclaimer:
          'No tenemos control ni asumimos ninguna responsabilidad por el contenido, las políticas de privacidad o las prácticas de sitios o servicios de terceros.',
      },
      childrens_privacy: {
        title: 'Privacidad de los Niños',
        description: 'Nuestro Servicio no está dirigido a personas menores de 18 años ("Niños").',
        parental_notice:
          'No recopilamos conscientemente información de identificación personal de personas menores de 18 años. Si es padre o tutor y sabe que sus Niños nos han proporcionado Datos Personales, comuníquese con nosotros. Si nos damos cuenta de que hemos recopilado Datos Personales de niños sin verificación del consentimiento de los padres, tomamos medidas para eliminar esa información de nuestros servidores.',
      },
      changes_to_policy: {
        title: 'Cambios a Esta Política de Privacidad',
        notification:
          'Podemos actualizar nuestra Política de Privacidad de vez en cuando. Le notificaremos cualquier cambio publicando la nueva Política de Privacidad en esta página.',
        effective_date:
          'Le informaremos por correo electrónico y/o un aviso destacado en nuestro Servicio, antes de que el cambio entre en vigencia y actualizaremos la "fecha de vigencia" en la parte superior de esta Política de Privacidad.',
        review_advice:
          'Se le aconseja que revise esta Política de Privacidad periódicamente para cualquier cambio. Los cambios a esta Política de Privacidad son efectivos cuando se publican en esta página.',
      },
      contact_us: {
        title: 'Contáctenos',
        description:
          'Si tiene alguna pregunta sobre esta Política de Privacidad, comuníquese con nosotros:',
        email: 'Por correo electrónico: pako@total-factory.com',
        website: 'Visitando esta página en nuestro sitio web: panel-track.com',
      },
    },
  },
  fr: {
    language: 'Choisir la langue',
    privacy_policy: {
      title: 'Politique de Confidentialité',
      effective_date: "Date d'effet: 3 avril 2025",
      intro: {
        company:
          'Panel Track LLC ("nous" ou "notre") exploite le site web panel-track.com et l\'application mobile PanelTrack (le "Service").',
        explanation:
          "Cette page vous informe de nos politiques concernant la collecte, l'utilisation et la divulgation des données personnelles lorsque vous utilisez notre Service ainsi que les choix dont vous disposez concernant ces données.",
        agreement:
          "Nous utilisons vos données pour fournir et améliorer le Service. En utilisant le Service, vous acceptez la collecte et l'utilisation d'informations conformément à cette politique. Sauf définition contraire dans cette Politique de Confidentialité, les termes utilisés dans cette Politique de Confidentialité ont la même signification que dans nos Conditions Générales.",
      },
      information_collection: {
        title: 'Collecte et Utilisation des Informations',
        description:
          "Nous collectons plusieurs types d'informations à diverses fins pour fournir et améliorer notre Service.",
      },
      types_of_data: {
        title: 'Types de Données Collectées',
        personal_data: {
          title: 'Données Personnelles',
          description:
            'Lors de l\'utilisation de notre Service, nous pouvons vous demander de nous fournir certaines informations personnellement identifiables qui peuvent être utilisées pour vous contacter ou vous identifier ("Données Personnelles"). Les informations personnellement identifiables peuvent inclure, mais ne sont pas limitées à:',
          items: ['Adresse e-mail', "Cookies et Données d'Utilisation"],
        },
        usage_data: {
          title: "Données d'Utilisation",
          description_browser:
            "Nous pouvons également collecter des informations que votre navigateur envoie chaque fois que vous visitez notre Service ou lorsque vous y accédez par le biais d'un appareil mobile (\"Données d'Utilisation\"). Ces Données d'Utilisation peuvent inclure des informations telles que l'adresse de protocole Internet de votre ordinateur (par exemple, adresse IP), le type de navigateur, la version du navigateur, les pages de notre Service que vous visitez, l'heure et la date de votre visite, le temps passé sur ces pages, les identifiants uniques de l'appareil et d'autres données de diagnostic.",
          description_mobile:
            "Lorsque vous accédez au Service par le biais d'un appareil mobile, ces Données d'Utilisation peuvent inclure des informations telles que le type d'appareil mobile que vous utilisez, l'identifiant unique de votre appareil mobile, l'adresse IP de votre appareil mobile, votre système d'exploitation mobile, le type de navigateur Internet mobile que vous utilisez, les identifiants uniques de l'appareil et d'autres données de diagnostic.",
        },
        tracking_cookies: {
          title: 'Données de Suivi et Cookies',
          description:
            "Nous utilisons des cookies et des technologies de suivi similaires pour suivre l'activité sur notre Service et conserver certaines informations.",
          explanation:
            'Les cookies sont des fichiers contenant une petite quantité de données qui peuvent inclure un identifiant unique anonyme. Les cookies sont envoyés à votre navigateur depuis un site web et stockés sur votre appareil. Les technologies de suivi également utilisées sont les balises, les tags et les scripts pour collecter et suivre les informations et pour améliorer et analyser notre Service.',
          browser_instructions:
            "Vous pouvez demander à votre navigateur de refuser tous les cookies ou d'indiquer quand un cookie est envoyé. Cependant, si vous n'acceptez pas les cookies, vous ne pourrez peut-être pas utiliser certaines parties de notre Service.",
          cookie_types: {
            session: {
              title: 'Cookies de Session',
              description:
                'Nous utilisons des Cookies de Session pour faire fonctionner notre Service.',
            },
            preference: {
              title: 'Cookies de Préférence',
              description:
                'Nous utilisons des Cookies de Préférence pour mémoriser vos préférences et divers paramètres.',
            },
            security: {
              title: 'Cookies de Sécurité',
              description: 'Nous utilisons des Cookies de Sécurité à des fins de sécurité.',
            },
          },
        },
      },
      use_of_data: {
        title: 'Utilisation des Données',
        purposes: [
          'Pour fournir et maintenir notre Service',
          'Pour vous informer des changements apportés à notre Service',
          'Pour vous permettre de participer aux fonctionnalités interactives de notre Service lorsque vous le souhaitez',
          'Pour fournir un service client et une assistance',
          'Pour fournir des analyses ou des informations précieuses afin que nous puissions améliorer le Service',
          "Pour surveiller l'utilisation du Service",
          'Pour détecter, prévenir et résoudre les problèmes techniques',
        ],
      },
      transfer_of_data: {
        title: 'Transfert de Données',
        description:
          'Vos informations, y compris les Données Personnelles, peuvent être transférées et conservées sur des ordinateurs situés en dehors de votre état, province, pays ou autre juridiction gouvernementale où les lois sur la protection des données peuvent différer de celles de votre juridiction.',
        international:
          "Si vous êtes situé en dehors des États-Unis d'Amérique et que vous choisissez de nous fournir des informations, veuillez noter que nous transférons les données, y compris les Données Personnelles, aux États-Unis d'Amérique et les traitons là-bas.",
        consent:
          'Votre consentement à cette Politique de Confidentialité suivi de votre soumission de ces informations représente votre accord à ce transfert.',
        security:
          "Panel Track LLC prendra toutes les mesures raisonnablement nécessaires pour garantir que vos données sont traitées en toute sécurité et conformément à cette Politique de Confidentialité et aucun transfert de vos Données Personnelles n'aura lieu vers une organisation ou un pays à moins qu'il existe des contrôles adéquats, y compris la sécurité de vos données et autres informations personnelles.",
      },
      disclosure_of_data: {
        title: 'Divulgation des Données',
        legal_requirements: {
          title: 'Exigences Légales',
          description:
            'Panel Track LLC peut divulguer vos Données Personnelles si elle croit de bonne foi que cette action est nécessaire pour:',
          reasons: [
            'Se conformer à une obligation légale',
            'Protéger et défendre les droits ou la propriété de Panel Track LLC',
            "Prévenir ou enquêter sur d'éventuels actes répréhensibles en rapport avec le Service",
            'Protéger la sécurité personnelle des utilisateurs du Service ou du public',
            'Se protéger contre la responsabilité juridique',
          ],
        },
      },
      security_of_data: {
        title: 'Sécurité des Données',
        description:
          "La sécurité de vos données est importante pour nous, mais n'oubliez pas qu'aucune méthode de transmission sur Internet ou méthode de stockage électronique n'est sécurisée à 100%. Bien que nous nous efforcions d'utiliser des moyens commercialement acceptables pour protéger vos Données Personnelles, nous ne pouvons pas garantir leur sécurité absolue.",
      },
      service_providers: {
        title: 'Prestataires de Services',
        description:
          'Nous pouvons faire appel à des sociétés tierces et à des particuliers pour faciliter notre Service ("Prestataires de Services"), pour fournir le Service en notre nom, pour exécuter des services liés au Service ou pour nous aider à analyser comment notre Service est utilisé.',
        obligations:
          "Ces tiers n'ont accès à vos Données Personnelles que pour effectuer ces tâches en notre nom et sont tenus de ne pas les divulguer ni les utiliser à d'autres fins.",
      },
      links_to_other_sites: {
        title: "Liens Vers D'autres Sites",
        description:
          "Notre Service peut contenir des liens vers d'autres sites qui ne sont pas exploités par nous. Si vous cliquez sur un lien tiers, vous serez dirigé vers le site de ce tiers. Nous vous conseillons vivement de consulter la Politique de Confidentialité de chaque site que vous visitez.",
        disclaimer:
          "Nous n'avons aucun contrôle et n'assumons aucune responsabilité quant au contenu, aux politiques de confidentialité ou aux pratiques des sites ou services tiers.",
      },
      childrens_privacy: {
        title: 'Confidentialité des Enfants',
        description:
          'Notre Service ne s\'adresse pas aux personnes de moins de 18 ans ("Enfants").',
        parental_notice:
          "Nous ne collectons pas sciemment d'informations personnellement identifiables auprès de personnes de moins de 18 ans. Si vous êtes un parent ou un tuteur et que vous savez que votre Enfant nous a fourni des Données Personnelles, veuillez nous contacter. Si nous apprenons que nous avons collecté des Données Personnelles d'enfants sans vérification du consentement parental, nous prenons des mesures pour supprimer ces informations de nos serveurs.",
      },
      changes_to_policy: {
        title: 'Modifications de Cette Politique de Confidentialité',
        notification:
          'Nous pouvons mettre à jour notre Politique de Confidentialité de temps à autre. Nous vous informerons de tout changement en publiant la nouvelle Politique de Confidentialité sur cette page.',
        effective_date:
          'Nous vous informerons par e-mail et/ou par un avis bien visible sur notre Service, avant que le changement ne devienne effectif, et mettrons à jour la "date d\'effet" en haut de cette Politique de Confidentialité.',
        review_advice:
          "Il vous est conseillé de consulter périodiquement cette Politique de Confidentialité pour tout changement. Les modifications apportées à cette Politique de Confidentialité sont effectives lorsqu'elles sont publiées sur cette page.",
      },
      contact_us: {
        title: 'Nous Contacter',
        description:
          'Si vous avez des questions concernant cette Politique de Confidentialité, veuillez nous contacter:',
        email: 'Par e-mail: pako@total-factory.com',
        website: 'En visitant cette page sur notre site web: panel-track.com',
      },
    },
  },
  pt: {
    language: 'Escolher o idioma',
    privacy_policy: {
      title: 'Política de Privacidade',
      effective_date: 'Data de vigência: 3 de abril de 2025',
      intro: {
        company:
          'Panel Track LLC ("nós" ou "nosso") opera o site web panel-track.com e o aplicativo móvel PanelTrack (o "Serviço").',
        explanation:
          'Esta página informa você sobre nossas políticas relativas à coleta, uso e divulgação de dados pessoais quando você utiliza nosso Serviço e as opções que você tem em relação a esses dados.',
        agreement:
          'Utilizamos seus dados para fornecer e melhorar o Serviço. Ao utilizar o Serviço, você concorda com a coleta e uso de informações de acordo com esta política. A menos que definido de outra forma nesta Política de Privacidade, os termos utilizados nesta Política de Privacidade têm o mesmo significado que em nossos Termos e Condições.',
      },
      information_collection: {
        title: 'Coleta e Uso de Informações',
        description:
          'Coletamos vários tipos de informações para diversos fins a fim de fornecer e melhorar nosso Serviço.',
      },
      types_of_data: {
        title: 'Tipos de Dados Coletados',
        personal_data: {
          title: 'Dados Pessoais',
          description:
            'Durante a utilização do nosso Serviço, podemos solicitar que você nos forneça algumas informações pessoalmente identificáveis que podem ser utilizadas para contatá-lo ou identificá-lo ("Dados Pessoais"). As informações pessoalmente identificáveis podem incluir, mas não se limitam a:',
          items: ['Endereço de e-mail', 'Cookies e Dados de Uso'],
        },
        usage_data: {
          title: 'Dados de Uso',
          description_browser:
            'Também podemos coletar informações que seu navegador envia sempre que você visita nosso Serviço ou quando você o acessa através de um dispositivo móvel ("Dados de Uso"). Esses Dados de Uso podem incluir informações como o endereço de Protocolo de Internet do seu computador (ex.: endereço IP), tipo de navegador, versão do navegador, as páginas do nosso Serviço que você visita, a hora e data da sua visita, o tempo gasto nessas páginas, identificadores únicos do dispositivo e outros dados de diagnóstico.',
          description_mobile:
            'Quando você acessa o Serviço por meio de um dispositivo móvel, esses Dados de Uso podem incluir informações como o tipo de dispositivo móvel que você usa, o identificador único do seu dispositivo móvel, o endereço IP do seu dispositivo móvel, seu sistema operacional móvel, o tipo de navegador de Internet móvel que você usa, identificadores únicos do dispositivo e outros dados de diagnóstico.',
        },
        tracking_cookies: {
          title: 'Dados de Rastreamento e Cookies',
          description:
            'Utilizamos cookies e tecnologias de rastreamento semelhantes para acompanhar a atividade em nosso Serviço e armazenar certas informações.',
          explanation:
            'Cookies são arquivos contendo uma pequena quantidade de dados que podem incluir um identificador único anônimo. Os cookies são enviados para o seu navegador a partir de um site e armazenados em seu dispositivo. As tecnologias de rastreamento também utilizadas são sinalizadores, tags e scripts para coletar e rastrear informações e para melhorar e analisar nosso Serviço.',
          browser_instructions:
            'Você pode instruir seu navegador a recusar todos os cookies ou a indicar quando um cookie está sendo enviado. No entanto, se você não aceitar cookies, pode não conseguir utilizar algumas partes do nosso Serviço.',
          cookie_types: {
            session: {
              title: 'Cookies de Sessão',
              description: 'Utilizamos Cookies de Sessão para operar nosso Serviço.',
            },
            preference: {
              title: 'Cookies de Preferência',
              description:
                'Utilizamos Cookies de Preferência para lembrar suas preferências e várias configurações.',
            },
            security: {
              title: 'Cookies de Segurança',
              description: 'Utilizamos Cookies de Segurança para fins de segurança.',
            },
          },
        },
      },
      use_of_data: {
        title: 'Uso dos Dados',
        purposes: [
          'Para fornecer e manter nosso Serviço',
          'Para notificá-lo sobre mudanças em nosso Serviço',
          'Para permitir que você participe de recursos interativos do nosso Serviço quando desejar',
          'Para fornecer suporte ao cliente e assistência',
          'Para fornecer análises ou informações valiosas para que possamos melhorar o Serviço',
          'Para monitorar o uso do Serviço',
          'Para detectar, prevenir e resolver problemas técnicos',
        ],
      },
      transfer_of_data: {
        title: 'Transferência de Dados',
        description:
          'Suas informações, incluindo Dados Pessoais, podem ser transferidas e mantidas em computadores localizados fora do seu estado, província, país ou outra jurisdição governamental onde as leis de proteção de dados podem diferir das da sua jurisdição.',
        international:
          'Se você está localizado fora dos Estados Unidos da América e opta por nos fornecer informações, observe que transferimos os dados, incluindo Dados Pessoais, para os Estados Unidos da América e os processamos lá.',
        consent:
          'O seu consentimento a esta Política de Privacidade, seguido pela submissão de tais informações, representa seu acordo com essa transferência.',
        security:
          'O Panel Track LLC tomará todas as medidas razoavelmente necessárias para garantir que seus dados sejam tratados com segurança e de acordo com esta Política de Privacidade e nenhuma transferência de seus Dados Pessoais ocorrerá para uma organização ou país a menos que haja controles adequados, incluindo a segurança de seus dados e outras informações pessoais.',
      },
      disclosure_of_data: {
        title: 'Divulgação de Dados',
        legal_requirements: {
          title: 'Requisitos Legais',
          description:
            'O Panel Track LLC pode divulgar seus Dados Pessoais se acreditar de boa fé que tal ação é necessária para:',
          reasons: [
            'Cumprir uma obrigação legal',
            'Proteger e defender os direitos ou propriedade do Panel Track LLC',
            'Prevenir ou investigar possíveis irregularidades em conexão com o Serviço',
            'Proteger a segurança pessoal dos usuários do Serviço ou do público',
            'Proteger-se contra responsabilidade legal',
          ],
        },
      },
      security_of_data: {
        title: 'Segurança dos Dados',
        description:
          'A segurança dos seus dados é importante para nós, mas lembre-se que nenhum método de transmissão pela Internet ou método de armazenamento eletrônico é 100% seguro. Embora nos esforcemos para usar meios comercialmente aceitáveis para proteger seus Dados Pessoais, não podemos garantir sua segurança absoluta.',
      },
      service_providers: {
        title: 'Prestadores de Serviço',
        description:
          'Podemos empregar empresas terceirizadas e indivíduos para facilitar nosso Serviço ("Prestadores de Serviço"), fornecer o Serviço em nosso nome, executar serviços relacionados ao Serviço ou nos ajudar a analisar como nosso Serviço é utilizado.',
        obligations:
          'Esses terceiros têm acesso aos seus Dados Pessoais apenas para realizar essas tarefas em nosso nome e são obrigados a não divulgá-los ou usá-los para qualquer outro propósito.',
      },
      links_to_other_sites: {
        title: 'Links Para Outros Sites',
        description:
          'Nosso Serviço pode conter links para outros sites que não são operados por nós. Se você clicar em um link de terceiros, será direcionado para o site desse terceiro. Recomendamos fortemente que você revise a Política de Privacidade de cada site que visitar.',
        disclaimer:
          'Não temos controle e não assumimos responsabilidade pelo conteúdo, políticas de privacidade ou práticas de sites ou serviços de terceiros.',
      },
      childrens_privacy: {
        title: 'Privacidade de Crianças',
        description: 'Nosso Serviço não se destina a pessoas com menos de 18 anos ("Crianças").',
        parental_notice:
          'Não coletamos intencionalmente informações pessoalmente identificáveis de pessoas com menos de 18 anos. Se você é pai ou responsável e sabe que sua Criança nos forneceu Dados Pessoais, entre em contato conosco. Se descobrirmos que coletamos Dados Pessoais de crianças sem verificação do consentimento dos pais, tomamos medidas para remover essas informações de nossos servidores.',
      },
      changes_to_policy: {
        title: 'Alterações a Esta Política de Privacidade',
        notification:
          'Podemos atualizar nossa Política de Privacidade periodicamente. Notificaremos você sobre quaisquer alterações publicando a nova Política de Privacidade nesta página.',
        effective_date:
          'Notificaremos você por e-mail e/ou um aviso em destaque em nosso Serviço, antes que a alteração se torne efetiva, e atualizaremos a "data de vigência" no topo desta Política de Privacidade.',
        review_advice:
          'Recomendamos que você revise periodicamente esta Política de Privacidade para verificar quaisquer alterações. As alterações a esta Política de Privacidade são efetivas quando publicadas nesta página.',
      },
      contact_us: {
        title: 'Entre em Contato Conosco',
        description:
          'Se você tiver alguma dúvida sobre esta Política de Privacidade, entre em contato conosco:',
        email: 'Por e-mail: pako@total-factory.com',
        website: 'Visitando esta página em nosso site: panel-track.com',
      },
    },
  },
}
